import { type PanelManager } from '@databases/panel-types';
import {
  type SelectionsApiContract,
  type ResolvedValue
} from '@databases/selections-api-types';
import { type ViewChangeType } from '@databases/utils/database/view/view';
import { proxy, ref } from 'valtio';

import { type SelectEntriesActionsAPI } from '../actions/entries/select-entries-actions';
import { type SelectFieldActionsAPI } from '../actions/fields/select-fields-actions';
import { type SelectValuesActionsAPI } from '../actions/values/select-values-actions';

export type SelectFieldActionsAPIFactory = (
  setViewChange: (view: ViewChangeType) => void
) => SelectFieldActionsAPI;

export type ActionBarActions =
  | SelectEntriesActionsAPI
  | SelectValuesActionsAPI
  | SelectFieldActionsAPIFactory;

/**
 * Partial props passed from the effect.
 * The portal will use hooks to fetch the remaining props.
 */
export interface PartialActionBarProps {
  actions: ActionBarActions;
  canEditContent: boolean;
  canMoveDown: boolean;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  canMoveUp: boolean;
  contentId: string;
  count: number;
  fieldsWithSelectionsCount: number;
  isDatabaseLocked: boolean;
  isDeleteColumnsDisabled: boolean;
  isDependencyFieldSelected: boolean;
  panelManager: PanelManager;
  selectedEntriesCount: number;
  selectedValues: ResolvedValue[];
  selectionMode: 'row' | 'column' | 'cell';
  selectionsApi: SelectionsApiContract;
  shouldShowClearCells: boolean;
}

/**
 * Reactive state for the ActionBarPortal.
 * The effect calls show/hide, and the portal component subscribes via useSnapshot.
 */
export const actionBarPortalState = proxy<{
  isVisible: boolean;
  props: PartialActionBarProps | null;
}>({
  isVisible: false,
  props: null
});

/**
 * Shows the action bar with partial props (effect provides selection data + business rules).
 * Note: selectionsApi, actions, and panelManager are wrapped in ref()
 * to prevent valtio from proxying them, which would break private field access and function references.
 */
export function showActionBar(props: PartialActionBarProps) {
  actionBarPortalState.isVisible = true;
  actionBarPortalState.props = {
    ...props,
    selectionsApi: ref(props.selectionsApi) as SelectionsApiContract,
    panelManager: ref(props.panelManager) as PanelManager,
    actions: ref(props.actions) as ActionBarActions
  };
}

/**
 * Hides the action bar.
 */
export function hideActionBar() {
  actionBarPortalState.isVisible = false;
  actionBarPortalState.props = null;
}
